/**
 * A utility type that adds a unique "brand" to a type, effectively distinguishing it from other types
 * with the same structure. This is typically used to create distinct types based on underlying types
 * (e.g., differentiating between `UserId` and `ProductId` even if they are both `string`).
 *
 * @typeparam Type - The base type that you want to brand. The original type of the value.
 * @typeparam TIdentifier - The unique identifier (symbol, string, or number) that acts as the brand and as description. Is not data!!
 *
 * @example
 * type UserId = Brand<string, 'UserId'>;
 * type ProductId = Brand<string, 'ProductId'>;
 *
 * @example
 * type First = Brand<number, 1>;
 * type Second = Brand<number, 2>;
 *
 * @example
 * type First = Brand<number, 'FIRST'>;
 * type Second = Brand<number, 'SECOND'>;
 *
 * @example
 * type Enabled = Brand<boolean, true>;
 * type Disabled = Brand<boolean, false>;
 *
 * @example
 * type Active = Brand<boolean, 'BOOLEAN_ACTIVE'>;
 * type Inactive = Brand<boolean, 'BOOLEAN_INACTIVE'>;
 *
 * @example
 * type On = Brand<boolean, 1>;
 * type Off = Brand<boolean, 0>;
 *
 * @example
 * export interface PcvizWorkspaceStationTypes {
 *    GLS: Brand<string, 'GLS'>;
 *    CR: Brand<string, 'CR'>;
 * }
 *
 * export type PcvizWorkspaceStationTypeValues = PcvizWorkspaceStationTypes[keyof PcvizWorkspaceStationTypes];
 *
 * const stationTypes: PcvizWorkspaceStationTypes = getStationTypesFromATrustedSource(); // as core or server
 *
 * function setStationType(stationType: PcvizWorkspaceStationTypeValues) {
 *
 * }
 *
 * function setStringStationType(stationType: string) {
 *
 * }
 *
 * setStationType(stationTypes.GLS); // Ok!
 *
 * setStationType('GLS'); // Type error, 'GLS' is not a trusted value
 *
 * setStationType('GLS' as PcvizWorkspaceStationTypeValues); // Dirty trick, types should only be setted in origin
 *
 *  setStringStationType('CR_STATION'); // Runtime error 'CR_STATION' is not a valid value
 *
 * @summary
 * * Each branded type can be treated as distinct types even though they are of the same type
 * * Ensures one single source of truth
 * * Ensures type and data integrity
 *
 */

export type Brand<Type extends string | number | boolean, TIdentifier extends string | number | boolean> = Type & {
    readonly __brand: TIdentifier;
};
